import Logo from '@/components/logo';
import {
    Code,
    Cpu,
    Database,
    Globe,
    Lock,
    LogIn,
    Server,
    Shield,
    UserPlus,
    Zap,
} from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './main.css';
import { Button } from '@/components/ui/button';

interface TechnologyCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    features: string[];
}

const TechnologyCard: React.FC<TechnologyCardProps> = ({
    icon,
    title,
    description,
    features,
}) => (
    <div className='tech-card'>
        <div className='tech-icon'>{icon}</div>
        <h3 className='tech-title'>{title}</h3>
        <p className='tech-description'>{description}</p>
        <ul className='tech-features'>
            {features.map((feature, index) => (
                <li
                    key={index}
                    className='tech-feature'>
                    {feature}
                </li>
            ))}
        </ul>
    </div>
);

const HomePage: React.FC = () => {
    const navigate = useNavigate();

    const handleLogin = () => {
        navigate('/login');
    };

    const handleSignup = () => {
        navigate('/signup');
    };

    const technologies = [
        {
            icon: <Code size={32} />,
            title: 'React.js',
            description:
                'A powerful JavaScript library for building modern, interactive user interfaces with component-based architecture.',
            features: [
                'Component-based architecture for reusable UI',
                'Virtual DOM for optimal performance',
                'Hooks for state and lifecycle management',
                'TypeScript support for type safety',
            ],
        },
        {
            icon: <Zap size={32} />,
            title: 'Axios',
            description:
                'Promise-based HTTP client for making API requests with excellent error handling and request/response interception.',
            features: [
                'Automatic JSON data transformation',
                'Request and response interception',
                'Client-side XSRF protection',
                'Promise-based async handling',
            ],
        },
        {
            icon: <Database size={32} />,
            title: 'PostgreSQL',
            description:
                'Advanced open-source relational database system with robust session storage capabilities and ACID compliance.',
            features: [
                'ACID compliance for data integrity',
                'JSONB support for flexible session data',
                'Advanced indexing for fast queries',
                'Secure connection pooling',
            ],
        },
        {
            icon: <Server size={32} />,
            title: 'ExpressJS',
            description:
                'Minimal and flexible Node.js web application framework providing robust features for building secure APIs.',
            features: [
                'Middleware support for request processing',
                'RESTful API development',
                'Error handling middleware',
                'CORS and security configuration',
            ],
        },
        {
            icon: <Lock size={32} />,
            title: 'Passport.js',
            description:
                'Authentication middleware for Node.js supporting multiple authentication strategies with session management.',
            features: [
                'Multiple authentication strategies',
                'Session-based authentication',
                'OAuth and JWT support',
                'Extensible architecture',
            ],
        },
        {
            icon: <Globe size={32} />,
            title: 'Full-Stack Integration',
            description:
                'Seamless integration between frontend and backend technologies creating a powerful session management system.',
            features: [
                'Real-time session synchronization',
                'Secure API communication',
                'Optimized data flow',
                'Scalable architecture',
            ],
        },
    ];

    const benefits = [
        {
            icon: <Shield size={24} />,
            title: 'Enhanced Security',
            description:
                'Multi-layered security with Passport.js authentication and secure session storage',
        },
        {
            icon: <Cpu size={24} />,
            title: 'High Performance',
            description:
                'Optimized database queries and efficient React rendering for fast user experiences',
        },
        {
            icon: <Database size={24} />,
            title: 'Data Integrity',
            description:
                'ACID-compliant PostgreSQL ensures reliable session data storage and retrieval',
        },
        {
            icon: <Zap size={24} />,
            title: 'Scalability',
            description:
                'Modular architecture allows easy scaling and addition of new features',
        },
    ];

    return (
        <div className='home-page'>
            <section className='hero-section !px-8'>
                <div className='hero-content'>
                    <Logo size='220px' />
                    <h1 className='hero-title'>Advanced Sessions Management</h1>
                    <p className='hero-subtitle'>
                        A full-stack solution leveraging modern technologies for
                        secure, scalable, and efficient session management
                    </p>

                    <div className='hero-auth-buttons'>
                        <Button
                            className='btn-login flex justify-center h-10 w-32'
                            onClick={handleLogin}>
                            <LogIn size={20} />
                            Login
                        </Button>
                        <Button
                            className='btn-signup flex justify-center h-10 w-32'
                            onClick={handleSignup}>
                            <UserPlus size={20} />
                            Sign Up
                        </Button>
                    </div>

                    <div className='hero-badges'>
                        <span className='badge frontend'>Frontend</span>
                        <span className='badge backend'>Backend</span>
                        <span className='badge database'>Database</span>
                        <span className='badge security'>Security</span>
                    </div>
                </div>
            </section>

            <section className='benefits-section px-4'>
                <div className='container'>
                    <h2 className='section-title'>Why This Stack?</h2>
                    <div className='benefits-grid'>
                        {benefits.map((benefit, index) => (
                            <div
                                key={index}
                                className='benefit-card'>
                                <div className='benefit-icon'>
                                    {benefit.icon}
                                </div>
                                <h3 className='benefit-title'>
                                    {benefit.title}
                                </h3>
                                <p className='benefit-description'>
                                    {benefit.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className='technologies-section px-4'>
                <div className='container'>
                    <h2 className='section-title'>Technology Stack</h2>
                    <p className='section-subtitle'>
                        A carefully selected combination of technologies working
                        together to provide a robust session management system
                    </p>
                    <div className='technologies-grid'>
                        {technologies.map((tech, index) => (
                            <TechnologyCard
                                key={index}
                                icon={tech.icon}
                                title={tech.title}
                                description={tech.description}
                                features={tech.features}
                            />
                        ))}
                    </div>
                </div>
            </section>

            <section className='architecture-section'>
                <div className='container'>
                    <h2 className='section-title'>System Architecture</h2>
                    <div className='architecture-flow'>
                        <div className='flow-step'>
                            <div className='flow-icon'>
                                <Code size={24} />
                            </div>
                            <div className='flow-content'>
                                <h3>Frontend (React + Axios)</h3>
                                <p>
                                    User interface built with React.js, making
                                    secure API calls via Axios
                                </p>
                            </div>
                        </div>

                        <div className='flow-arrow'>→</div>

                        <div className='flow-step'>
                            <div className='flow-icon'>
                                <Server size={24} />
                            </div>
                            <div className='flow-content'>
                                <h3>Backend (Express + Passport)</h3>
                                <p>
                                    Express.js server handling requests with
                                    Passport.js authentication
                                </p>
                            </div>
                        </div>

                        <div className='flow-arrow'>→</div>

                        <div className='flow-step'>
                            <div className='flow-icon'>
                                <Database size={24} />
                            </div>
                            <div className='flow-content'>
                                <h3>Database (PostgreSQL)</h3>
                                <p>
                                    Secure session storage and user data
                                    management
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className='cta-section'>
                <div className='container'>
                    <h2 className='cta-title'>Ready to Get Started?</h2>
                    <p className='cta-description'>
                        Join thousands, who trust our advanced session
                        management system
                    </p>
                    <div className='cta-buttons'>
                        <Button
                            className='btn btn-primary h-10'
                            onClick={handleSignup}>
                            <UserPlus size={20} />
                            Create Account
                        </Button>
                        <Button
                            className='btn btn-secondary h-10'
                            onClick={handleLogin}>
                            <LogIn size={20} />
                            Existing User Login
                        </Button>
                    </div>

                    <div className='quick-stats'>
                        <div className='stat'>
                            <div className='stat-number'>10K+</div>
                            <div className='stat-label'>Active Users</div>
                        </div>
                        <div className='stat'>
                            <div className='stat-number'>99.9%</div>
                            <div className='stat-label'>Uptime</div>
                        </div>
                        <div className='stat'>
                            <div className='stat-number'>24/7</div>
                            <div className='stat-label'>
                                Security Monitoring
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
